import React, { useEffect, useState } from "react"
import Popup from "component/popup/popup"
import { agent } from "apiClient/api"
import { useSnackbar } from "context/snackbarContext"
import SecretField from "./secretField"
import { ConfigurationUser, displayUserName, newPasswordError } from "./configurationUsers"
import styles from "./configuration.module.scss"

type ResetPasswordPopupProps = {
    isOpen: boolean
    user: ConfigurationUser | null
    onClose: () => void
    onUpdated: (newPassword: string) => void
    onCopy: (text: string, message: string) => void
}

const ResetPasswordPopup: React.FC<ResetPasswordPopupProps> = ({ isOpen, user, onClose, onUpdated, onCopy }) => {
    const { showError } = useSnackbar()
    const [currentPassword, setCurrentPassword] = useState("")
    const [isLoadingPassword, setIsLoadingPassword] = useState(false)
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [error, setError] = useState<string | undefined>()
    const [isSaving, setIsSaving] = useState(false)

    useEffect(() => {
        if (!isOpen || !user) return
        setCurrentPassword("")
        setNewPassword("")
        setConfirmPassword("")
        setError(undefined)
        setIsLoadingPassword(true)
        agent.UserPasswordAPI.get(user.private_user_id)
            .then((response) => {
                if (response?.status === true) {
                    setCurrentPassword(response?.data?.user_password || "")
                } else {
                    showError(response?.error?.message || "Failed to load current password")
                }
            })
            .catch((err) => {
                console.error("Get user password error", err)
                showError("Failed to load current password")
            })
            .finally(() => setIsLoadingPassword(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, user])

    const handleUpdate = async () => {
        if (!user) return
        const nextError = newPasswordError(newPassword, confirmPassword)
        setError(nextError)
        if (nextError) return

        setIsSaving(true)
        try {
            const response = await agent.UpdateUserPasswordAPI.put({
                private_user_id: user.private_user_id,
                user_password: newPassword,
            })
            if (response?.status === true) {
                onUpdated(newPassword)
            } else {
                showError(response?.error?.message || "Failed to update password")
            }
        } catch (err) {
            console.error("Update user password error", err)
            showError("Failed to update password")
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <Popup
            isOpen={isOpen}
            onClose={onClose}
            title={user ? `Manage Password - ${displayUserName(user)}` : "Manage Password"}
            size="medium"
            secondaryButton={{ label: "Cancel", onClick: onClose }}
            primaryButton={{ label: isSaving ? "Updating..." : "Update Password", onClick: handleUpdate, disabled: isSaving }}
            showCloseButton={false}
        >
            <div className={styles.formStack}>
                <SecretField
                    label="Current Password"
                    value={isLoadingPassword ? "" : currentPassword}
                    placeholder={isLoadingPassword ? "Loading..." : undefined}
                    readOnly
                    showCopy
                    onCopy={() => onCopy(currentPassword, "Password copied")}
                />
                <div>
                    <span className={styles.typeLabel}>Set New Password</span>
                    <div className={styles.formStack}>
                        <SecretField
                            label="Enter new password"
                            hideLabel
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            name="set-new-password"
                            autoComplete="new-password"
                        />
                        <SecretField
                            label="Confirm new password"
                            hideLabel
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            name="confirm-new-password"
                            autoComplete="new-password"
                            error={error}
                        />
                    </div>
                </div>
                <p className={styles.helperText}>
                    Password will be updated immediately. All the active sessions will be logged out.
                </p>
            </div>
        </Popup>
    )
}

export default ResetPasswordPopup
